import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Anchor,
  Button,
  FileInput,
  Group,
  Modal,
  Stack,
  Table,
  Text,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconCheck, IconDownload, IconFileSpreadsheet } from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { apiClient } from '../../api/client';

interface Props {
  opened: boolean;
  onClose: () => void;
}

interface ImportError {
  rowNumber: number;
  phone: string;
  message: string;
}

interface ImportResponse {
  totalRows: number;
  inserted: number;
  updated: number;
  skipped: number;
  errors: ImportError[];
}

const TEMPLATE_CSV =
  'Phone,FullName,Email\n' +
  '0599123456,أحمد خليل,ahmad@example.com\n0599765432,سناء عودة,\n';

export function CustomerImportModal({ opened, onClose }: Props) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<ImportResponse | null>(null);

  const reset = () => {
    setFile(null);
    setResult(null);
  };

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customers-template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const submit = async () => {
    if (!file) return;
    setSubmitting(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const response = await apiClient.post<ImportResponse>(
        '/api/customers/bulk-import',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      setResult(response.data);
      if (response.data.errors.length === 0) {
        void qc.invalidateQueries({ queryKey: ['customers'] });
        notifications.show({
          color: 'green',
          title: t('customers.import.successTitle'),
          message: t('customers.import.successMessage', {
            inserted: response.data.inserted,
            updated: response.data.updated,
          }),
        });
      }
    } catch (err) {
      const detail = isAxiosError(err)
        ? ((err.response?.data as { detail?: string; title?: string } | undefined)?.detail ??
           (err.response?.data as { title?: string } | undefined)?.title ??
           err.message)
        : t('common.toasts.errorBody');
      notifications.show({ color: 'red', title: t('customers.import.failTitle'), message: detail });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={() => { reset(); onClose(); }}
      title={t('customers.import.title')}
      size="lg"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">{t('customers.import.helpBody')}</Text>
        <Anchor size="sm" onClick={downloadTemplate}>
          <Group gap={4} component="span">
            <IconDownload size={14} stroke={1.6} />
            {t('customers.import.downloadTemplate')}
          </Group>
        </Anchor>
        <FileInput
          accept=".csv,text/csv"
          value={file}
          onChange={setFile}
          placeholder={t('customers.import.dropPrompt') ?? ''}
          leftSection={<IconFileSpreadsheet size={16} stroke={1.6} />}
          clearable
        />

        {result && result.errors.length === 0 && (
          <Alert color="green" icon={<IconCheck size={16} />}>
            {t('customers.import.successMessage', {
              inserted: result.inserted,
              updated: result.updated,
            })}
          </Alert>
        )}

        {result && result.errors.length > 0 && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            <Text size="sm" mb="xs">{t('customers.import.errorsIntro')}</Text>
            <Table verticalSpacing={4} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('customers.import.row')}</Table.Th>
                  <Table.Th>{t('customers.import.phone')}</Table.Th>
                  <Table.Th>{t('customers.import.error')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {result.errors.slice(0, 50).map((e) => (
                  <Table.Tr key={`${e.rowNumber}-${e.phone}`}>
                    <Table.Td className="font-numeric">{e.rowNumber}</Table.Td>
                    <Table.Td dir="ltr">{e.phone}</Table.Td>
                    <Table.Td>{e.message}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Alert>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={() => { reset(); onClose(); }}>
            {t('common.cancel')}
          </Button>
          <Button color="wheels" loading={submitting} disabled={!file} onClick={submit}>
            {t('customers.import.submit')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
